import { memo, useCallback, useMemo, useRef, useState, useLayoutEffect } from 'react'
import ReactDOM from 'react-dom'
import Icon24 from 'builder/components/Icon'
import { useClickOutsideExcept } from 'builder/hooks/useClickOutsideExcept'
import { FORMATS } from 'builder/modules/constants'
import { useI18n } from 'builder/hooks/useI18n'
import { useEscapeHandler } from 'builder/hooks/useEscapeHandler'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import { usePortalNode } from 'builder/hooks/usePortalNode'
import { reflow } from 'builder/utils/reflow'
import { DownloadDocumentHandler } from 'builder/modules/resumeEditor'
import {
  Container,
  Button,
  ActionMenuContainer,
  ActionMenuItem,
  IconContainer,
  IconChevronDown,
  ButtonContainer,
  Separator,
  MobileOverlay,
  MobileOptionsContainer,
  MobileOption,
  MobileCancelButton,
  ShareOption,
} from './styles'

const LOCALIZATION_KEY = 'builder.resume_editor.document_download'

export type Props = {
  handleShareDocument?: () => void
  handleCombineDocuments?: () => void
  handleDownloadDocument: DownloadDocumentHandler
  showResumeDistributionModal?: (format: FORMATS) => void
  isDownloading?: boolean
  handleDownloadButtonMenuOpen?: () => void
}

export const DownloadButton = memo(
  ({
    handleDownloadDocument,
    handleShareDocument,
    showResumeDistributionModal,
    isDownloading,
    handleCombineDocuments,
    handleDownloadButtonMenuOpen,
  }: Props) => {
    const { i18n } = useI18n()
    const [isDownloadMenuOpen, setIsDownloadMenuOpen] = useState<boolean>(false)
    const menuRef = useRef<HTMLDivElement | null>(null)
    const buttonRef = useRef<HTMLDivElement | null>(null)
    const isSharingEnabled = !!handleShareDocument
    const isCombineEnabled = !!handleCombineDocuments
    const { isPhone } = useMediaQueries()
    const portalNode = usePortalNode()
    const overlayRef = useRef<HTMLDivElement | null>(null)

    const handleButtonClick = useCallback(() => {
      if (!isDownloadMenuOpen && handleDownloadButtonMenuOpen) {
        handleDownloadButtonMenuOpen()
      }

      setIsDownloadMenuOpen(!isDownloadMenuOpen)
    }, [isDownloadMenuOpen, handleDownloadButtonMenuOpen])

    useClickOutsideExcept(menuRef, [buttonRef], () => {
      setIsDownloadMenuOpen(false)
    })

    const handleDownloadDocumentClick: DownloadDocumentHandler = useCallback(
      ({ format = FORMATS.pdf } = {}) => {
        setIsDownloadMenuOpen(false)

        if (showResumeDistributionModal) {
          showResumeDistributionModal(format)
          return
        }

        handleDownloadDocument({ format })
      },
      [handleDownloadDocument, showResumeDistributionModal],
    )

    const handleShareClick = useCallback(() => {
      setIsDownloadMenuOpen(false)

      if (handleShareDocument) handleShareDocument()
    }, [handleShareDocument])

    const handleCombineClick = useCallback(() => {
      setIsDownloadMenuOpen(false)
      if (handleCombineDocuments) handleCombineDocuments()
    }, [handleCombineDocuments])

    const closeMenu = useCallback(() => {
      setIsDownloadMenuOpen(false)
    }, [])

    useEscapeHandler(closeMenu)

    useLayoutEffect(() => {
      if (overlayRef.current) reflow(overlayRef.current)
      if (menuRef.current) reflow(menuRef.current)
    }, [])

    const MobileMenu = useMemo(() => {
      return ReactDOM.createPortal(
        <MobileOverlay ref={overlayRef} isVisible={isDownloadMenuOpen}>
          <MobileOptionsContainer ref={menuRef} isVisible={isDownloadMenuOpen}>
            <MobileOption onClick={() => handleDownloadDocumentClick({ format: FORMATS.pdf })}>
              <IconContainer>
                <Icon24.PdfCurrentColor />
              </IconContainer>
              {i18n.t(`${LOCALIZATION_KEY}.options.pdf`)}
            </MobileOption>
            <Separator />
            <MobileOption onClick={() => handleDownloadDocumentClick({ format: FORMATS.docx })}>
              <IconContainer>
                <Icon24.Docx />
              </IconContainer>
              {i18n.t(`${LOCALIZATION_KEY}.options.docx`)}
            </MobileOption>
            <Separator />
            <MobileOption onClick={() => handleDownloadDocumentClick({ format: FORMATS.txt })}>
              <IconContainer>
                <Icon24.Txt />
              </IconContainer>
              {i18n.t(`${LOCALIZATION_KEY}.options.txt`)}
            </MobileOption>
            {isCombineEnabled && (
              <MobileOption onClick={handleCombineClick}>
                <IconContainer>
                  <Icon24.Combine />
                </IconContainer>
                {i18n.t('builder.resume_editor.combine_with')}
              </MobileOption>
            )}
          </MobileOptionsContainer>
          {isSharingEnabled && (
            <ShareOption onClick={handleShareClick} ref={menuRef} isVisible={isDownloadMenuOpen}>
              <IconContainer>
                <Icon24.Link />
              </IconContainer>
              {i18n.t(`${LOCALIZATION_KEY}.options.share_resume`)}
            </ShareOption>
          )}
          <MobileCancelButton isVisible={isDownloadMenuOpen}>
            {i18n.t('builder.resume_editor.cancel')}
          </MobileCancelButton>
        </MobileOverlay>,
        portalNode,
      )
    }, [
      handleDownloadDocumentClick,
      handleShareClick,
      i18n,
      isDownloadMenuOpen,
      isSharingEnabled,
      portalNode,
      handleCombineClick,
      isCombineEnabled,
    ])

    const DesktopMenu = useMemo(() => {
      return (
        <ActionMenuContainer isVisible={isDownloadMenuOpen} ref={menuRef}>
          <ActionMenuItem onClick={() => handleDownloadDocumentClick({ format: FORMATS.pdf })}>
            <IconContainer>
              <Icon24.PdfCurrentColor />
            </IconContainer>
            {i18n.t(`${LOCALIZATION_KEY}.options.pdf`)}
          </ActionMenuItem>
          <ActionMenuItem onClick={() => handleDownloadDocumentClick({ format: FORMATS.docx })}>
            <IconContainer>
              <Icon24.Docx />
            </IconContainer>
            {i18n.t(`${LOCALIZATION_KEY}.options.docx`)}
          </ActionMenuItem>
          <ActionMenuItem onClick={() => handleDownloadDocumentClick({ format: FORMATS.txt })}>
            <IconContainer>
              <Icon24.Txt />
            </IconContainer>
            {i18n.t(`${LOCALIZATION_KEY}.options.txt`)}
          </ActionMenuItem>

          {isCombineEnabled && (
            <ActionMenuItem onClick={handleCombineClick}>
              <IconContainer>
                <Icon24.Combine />
              </IconContainer>
              {i18n.t('builder.resume_editor.combine_with')}
            </ActionMenuItem>
          )}

          {isSharingEnabled && <Separator />}
          {isSharingEnabled && (
            <ActionMenuItem onClick={handleShareClick}>
              <IconContainer>
                <Icon24.Link />
              </IconContainer>
              {i18n.t(`${LOCALIZATION_KEY}.options.share_resume`)}
            </ActionMenuItem>
          )}
        </ActionMenuContainer>
      )
    }, [
      handleDownloadDocumentClick,
      handleShareClick,
      i18n,
      isDownloadMenuOpen,
      isSharingEnabled,
      isCombineEnabled,
      handleCombineClick,
    ])

    return (
      <Container>
        <ButtonContainer ref={buttonRef}>
          <Button
            size="small"
            trailingIcon={
              <IconChevronDown
                style={{
                  transform: isDownloadMenuOpen ? 'rotateX(180deg)' : 'rotateX(0deg)',
                }}
              />
            }
            disabled={isDownloading}
            onClick={handleButtonClick}
          >
            {i18n.t(`${LOCALIZATION_KEY}.download_button_text`)}
          </Button>
        </ButtonContainer>
        {isPhone ? MobileMenu : DesktopMenu}
      </Container>
    )
  },
)
